import { ApiError } from '@/shared/data/client';
import { authUamErrorKinds } from '@flowqa/shared';
import { isAuthUamErrorKindMatch } from './authUamErrors';
import type { AuthSession } from './types';

export type RefreshOutcome = {
  session: AuthSession | null;
  didRefresh: boolean;
};

export const mergeSessionFallbacks = (
  refreshed: AuthSession,
  fallback: AuthSession | null,
): AuthSession => {
  if (!fallback) return refreshed;
  if (fallback.userId !== refreshed.userId) {
    return refreshed;
  }
  const clearedOrganisationContext =
    refreshed.orgId === undefined && refreshed.lastOrganisationId === null;
  return {
    ...refreshed,
    orgId: clearedOrganisationContext ? undefined : (refreshed.orgId ?? fallback.orgId),
    orgName: clearedOrganisationContext ? undefined : (refreshed.orgName ?? fallback.orgName),
    lastOrganisationId:
      refreshed.lastOrganisationId === undefined
        ? fallback.lastOrganisationId
        : refreshed.lastOrganisationId,
    lastProjectId:
      clearedOrganisationContext || refreshed.lastProjectId !== undefined
        ? refreshed.lastProjectId
        : fallback.lastProjectId,
    onboardingCompleted: refreshed.onboardingCompleted ?? fallback.onboardingCompleted,
    emailVerified: refreshed.emailVerified ?? fallback.emailVerified,
    roles: refreshed.roles ?? fallback.roles,
    superuserEmailMatched: refreshed.superuserEmailMatched ?? fallback.superuserEmailMatched,
  };
};

export const wait = async (ms: number): Promise<void> => {
  await new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
};

export const hasClerkClientSession = (client: unknown): boolean => {
  return resolveClerkSessionId(client) !== null;
};

const resolveClerkSessions = (client: unknown): unknown[] => {
  if (!client || typeof client !== 'object') {
    return [];
  }
  const sessionsRaw = (client as { sessions?: unknown }).sessions;
  return Array.isArray(sessionsRaw) ? sessionsRaw : [];
};

const resolveLastActiveClerkSessionId = (client: unknown): string | null => {
  if (!client || typeof client !== 'object') {
    return null;
  }
  const camelCaseSessionId = (client as { lastActiveSessionId?: unknown }).lastActiveSessionId;
  if (typeof camelCaseSessionId === 'string' && camelCaseSessionId.length > 0) {
    return camelCaseSessionId;
  }
  const snakeCaseSessionId = (client as { last_active_session_id?: unknown })
    .last_active_session_id;
  return typeof snakeCaseSessionId === 'string' && snakeCaseSessionId.length > 0
    ? snakeCaseSessionId
    : null;
};

const resolveSessionIdFromCandidate = (candidate: unknown): string | null => {
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const sessionId = (candidate as { id?: unknown }).id;
  return typeof sessionId === 'string' && sessionId.length > 0 ? sessionId : null;
};

const resolvePreferredClerkSession = (client: unknown): object | null => {
  const sessions = resolveClerkSessions(client);
  if (sessions.length === 0) {
    return null;
  }
  const lastActiveSessionId = resolveLastActiveClerkSessionId(client);
  const preferredSession = sessions.find((candidate) => {
    return resolveSessionIdFromCandidate(candidate) === lastActiveSessionId;
  });
  return preferredSession ?? sessions[0] ?? null;
};

export const resolveClerkSessionId = (client: unknown): string | null => {
  const preferredSessionId = resolveSessionIdFromCandidate(resolvePreferredClerkSession(client));
  if (preferredSessionId) {
    return preferredSessionId;
  }
  return resolveLastActiveClerkSessionId(client);
};

export const resolveTokenFromClerkClientSessions = async (
  client: unknown,
  options?: { skipCache?: boolean },
): Promise<string | null> => {
  const sessionCandidate = resolvePreferredClerkSession(client);
  if (!sessionCandidate || typeof sessionCandidate !== 'object') {
    return null;
  }
  const getTokenCandidate = (sessionCandidate as { getToken?: unknown }).getToken;
  if (typeof getTokenCandidate !== 'function') {
    return null;
  }
  try {
    const token = await (
      getTokenCandidate as (opts?: { skipCache?: boolean }) => Promise<string | null | undefined>
    )(options);
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
};

export const bootstrapTimeoutMs = 20_000;
export const bootstrapRetryIntervalMs = 400;

export const isAccountNotFoundError = (error: unknown): boolean =>
  error instanceof ApiError &&
  isAuthUamErrorKindMatch({
    error,
    expectedKind: authUamErrorKinds.accountNotFound,
    message: error.message,
  });

export const isIdentityMismatchError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  return isAuthUamErrorKindMatch({
    error,
    expectedKind: authUamErrorKinds.identityMismatch,
    message: error.message,
  });
};

export const isSessionTokenExpiredError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  return isAuthUamErrorKindMatch({
    error,
    expectedKind: authUamErrorKinds.sessionExpired,
    message: error.message,
  });
};

export const shouldPreserveSessionOnSignedOutSignal = (input: {
  hasSignedInSignal: boolean;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.hasSignedInSignal) return false;
  return input.currentSession !== null;
};

export const shouldPreserveSessionOnRefreshMiss = (input: {
  refreshedSession: AuthSession | null;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.refreshedSession) return false;
  return input.currentSession !== null;
};
